import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { BrowserRouter, useLocation } from "react-router-dom";
import Header from "./Components/Header";
import Router from "./Components/Router";
// import Sidebar from "./Components/Sidebar";
import MiniSidebar from "./Components/Sidebar/MiniSidebar";
import { useSelector, useDispatch } from "react-redux";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useMediaQuery } from "@mui/material";
import Fade from "@mui/material/Fade";
import Zoom from "@mui/material/Zoom";
import ScrollToTop from "./Components/common/ScrollToTop";
import ChatComponent from "./Components/AiChat/ChatComponent";
import { API_URL } from "./consts";
import axios from "axios";
// (other imports remain unchanged)
import GlobalPlusButton from "./Components/GlobalPlusButton";
import InterestsDialog from "./Components/Profile/InterestsDialog";
import OrganizationOnboardingDialog from "./Components/Organization/OrganizationOnboardingDialog";
import { popupTimer } from "./consts";
import { trackPageView } from "./utils/analytics";
import { storeProfileSafely, getStoredProfile } from "./utils/authUtils";

// Custom transition component that waits for positioning before animating
const TooltipTransition = React.forwardRef(function TooltipTransition(props, ref) {
  const { children, in: inProp, timeout = 200, ...other } = props;
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    if (inProp) {
      // Small delay to ensure Popper has calculated position
      const timer = setTimeout(() => setMounted(true), 10);
      return () => clearTimeout(timer);
    } else {
      setMounted(false);
    }
  }, [inProp]);
  
  return (
    <Fade
      ref={ref}
      in={inProp && mounted}
      timeout={timeout}
      {...other}
    >
      {children}
    </Fade>
  );
});

const theme = createTheme({
  palette: {
    primary: {
      light: "#fff",
      main: "rgb(0,202,153)",
      dark: "rgb(0,202,153)",
    },
    secondary: {
      main: "rgb(0,202,153)",
    },
  },
  components: {
    MuiIcon: {
      styleOverrides: {
        root: {
          color: "rgb(0,202,153) !important",
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        disablePortal: false,
        disableInteractive: false,
        enterDelay: 100,
        leaveDelay: 0,
        TransitionComponent: TooltipTransition,
        TransitionProps: {
          timeout: {
            enter: 200,
            exit: 150,
          },
        },
        PopperProps: {
          container: () => (typeof document !== 'undefined' ? document.body : null),
          placement: 'bottom',
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
            {
              name: 'preventOverflow',
              options: {
                rootBoundary: 'viewport',
                tether: true,
                tetherOffset: 8,
                padding: 8,
              },
            },
            {
              name: 'computeStyles',
              options: {
                gpuAcceleration: false,
                adaptive: true,
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top', 'bottom', 'right', 'left'],
              },
            },
          ],
        },
      },
      styleOverrides: {
        popper: {
          zIndex: 1300,
          position: 'fixed !important',
          '&[data-popper-placement]': {
            position: 'fixed !important',
          },
        },
        tooltip: {
          transform: 'none !important',
          willChange: 'opacity',
        },
      },
    },
  },
});

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Create a component that uses useLocation so it can decide whether to show the sidebar.
const AppContent = ({
  setProfile,
  sidebarOpen,
  setSidebarOpen,
  isMobile,
  popupTxt,
  isLocationAuth,
  authorized,
  setIsLocationAuth,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const hideSidebar = location.pathname === "/login" || location.pathname === "/info";
  const isStandalonePage = location.pathname === "/info";
  
  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  // Scroll detection for header hide/show - only on mobile and feed view pages
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const scrollThreshold = 10; // Minimum scroll distance to trigger hide/show

  useEffect(() => {
    // Only apply scroll behavior on mobile and on feed view pages
    const feedViewPages = ['/community', '/my-reviews', '/community-spaces'];
    const isFeedViewPage = feedViewPages.includes(location.pathname);
    
    // On desktop or non-feed pages, always show header
    if (!isMobile || !isFeedViewPage) {
      setIsHeaderVisible(true);
      lastScrollY.current = 0;
      return;
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDifference = Math.abs(currentScrollY - lastScrollY.current);

      // Only process if there's meaningful scroll movement
      if (scrollDifference < 5) {
        ticking.current = false;
        return;
      }

      // Show header when at top of page
      if (currentScrollY < scrollThreshold) {
        setIsHeaderVisible(true);
      } else {
        // Hide header when scrolling down, show when scrolling up
        if (currentScrollY > lastScrollY.current) {
          // Scrolling down
          setIsHeaderVisible(false);
        } else {
          // Scrolling up
          setIsHeaderVisible(true);
        }
      }

      lastScrollY.current = currentScrollY;
      ticking.current = false;
    };

    // Throttle scroll events using requestAnimationFrame for better performance
    const throttledHandleScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(handleScroll);
        ticking.current = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [isMobile, location.pathname]);

  return (
    <Box sx={{ display: "flex", paddingBottom: isStandalonePage ? 0 : "10px" }}>
      {/* Conditionally render the sidebar */}
      {!hideSidebar && (
        <MiniSidebar
          setProfile={setProfile}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          onMobilePlusClick={() => {
            // Trigger event to open plus dialog
            window.dispatchEvent(new CustomEvent('openPlusDialog'));
          }}
          onMobileChatClick={() => {
            // Trigger event to open chat
            window.dispatchEvent(new CustomEvent('openChatDialog'));
          }}
        />
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: "100%",
          minWidth: 0, // Prevents flexbox overflow issues
          paddingBottom: isStandalonePage ? 0 : (isMobile ? "80px" : "0"), // Add padding on mobile for bottom navigation
        }}
      >
        {/* Render header and snackbar only if not in location-auth mode and not standalone page */}
        {!isLocationAuth && !isStandalonePage && (
          <>
            <Snackbar
              open={!!popupTxt.text}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Alert severity={popupTxt.type} sx={{ width: "100%" }}>
                {popupTxt.text}
              </Alert>
            </Snackbar>
            <Header
              setProfile={setProfile}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              isVisible={isHeaderVisible}
            />
          </>
        )}
        <Router authorized={!isLocationAuth} user={authorized} setIsLocationAuth={setIsLocationAuth} />
        {/* Chat and Plus components - hidden on standalone pages and mobile (controlled by bottom nav) */}
        {authorized && !isStandalonePage && <ChatComponent onResponse={location.pathname.includes('/spaces/') ? (response) => {
          // Pass the response to Space component via Redux
          dispatch({ type: "AI_CHAT_RESPONSE", payload: response });
        } : undefined} />}
        {/* The plus button - FAB hidden on standalone pages and mobile (controlled by bottom nav) */}
        {authorized && !isStandalonePage && <GlobalPlusButton />}
        <ScrollToTop />
      </Box>
    </Box>
  );
};

const AppContentWithDialog = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const authorized = useSelector((state) => state.authorized);
  const token = useSelector((state) => state.token);
  const userData = useSelector((state) => state.user);
  const [interestsDialogOpen, setInterestsDialogOpen] = useState(false);
  const [isInterestsRequired, setIsInterestsRequired] = useState(false);
  const [interestsDismissed, setInterestsDismissed] = useState(false);
  const [organizationDialogOpen, setOrganizationDialogOpen] = useState(false);

  // Don't show dialog on login screen
  const shouldShowDialog = location.pathname !== "/login" && authorized;

  // Watch userData changes from Redux to show organization dialog first, then interests
  useEffect(() => {
    // Only proceed if we have all required conditions
    if (!authorized || !userData || !shouldShowDialog) {
      return;
    }
    // Wait for full profile from API - userData may be { token } from Router on login, avoid race
    if (!userData.id) {
      return;
    }

    // Priority 1: Check organization onboarding
    // Show dialog ONLY if part_of_organization is null (not false, not true, not undefined)
    // null = user hasn't answered yet
    // false = user answered "Private" - don't show again
    // true = user is part of organization - don't show
    if (userData.part_of_organization === null && !organizationDialogOpen) {
      setOrganizationDialogOpen(true);
      return; // Exit early to prevent showing interests dialog
    } 
    
    // Priority 2: Check interests (only if organization is set and not already dismissed this session)
    const hasInterests = Array.isArray(userData.most_interested) && userData.most_interested.length > 0;
    if (userData.part_of_organization !== null &&
        !organizationDialogOpen &&
        !interestsDismissed &&
        !hasInterests) {
      setIsInterestsRequired(true);
      setInterestsDialogOpen(true);
    }
  }, [userData, authorized, organizationDialogOpen, interestsDismissed, shouldShowDialog, location.pathname]);

  return (
    <>
      {/* Organization Onboarding Dialog - Shows when part_of_organization is null */}
      {shouldShowDialog && (
        <OrganizationOnboardingDialog
          open={organizationDialogOpen}
          onClose={() => {
            // Dialog cannot be closed without selection
          }}
          onComplete={async (orgData) => {
            try {
              // Fetch updated profile to get latest data
              const response = await axios.get(`${API_URL}/profile`, {
                withCredentials: true,
              });
              
              if (response.data?.profile) {
                const updatedProfile = response.data.profile;
                dispatch({ type: "SET_USER_DATA", payload: updatedProfile });
                storeProfileSafely(updatedProfile);
                setOrganizationDialogOpen(false);
                
                dispatch({
                  type: "SET_POPUP",
                  payload: {
                    text: orgData.part_of_organization 
                      ? "Organization created successfully!" 
                      : "Profile updated successfully!",
                    type: "success",
                  },
                });
                setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), popupTimer);
              }
            } catch (error) {
              console.error("Error fetching updated profile:", error);
              setOrganizationDialogOpen(false);
              dispatch({
                type: "SET_POPUP",
                payload: {
                  text: "Setup completed, but failed to refresh profile",
                  type: "warning",
                },
              });
              setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), popupTimer);
            }
          }}
        />
      )}

      {/* Interests Dialog - Shows on main page when most_interested is null */}
      {shouldShowDialog && (
        <InterestsDialog
          open={interestsDialogOpen}
          onClose={() => {
            setInterestsDismissed(true);
            setInterestsDialogOpen(false);
          }}
          onSave={async (interests) => {
            try {
              // SECURITY: Cookies are sent automatically, no Authorization header needed
              const response = await axios.put(
                `${API_URL}/profile`,
                { most_interested: interests },
                {
                  withCredentials: true, // Ensure cookies are sent
                  headers: {
                    "Content-Type": "application/json",
                  },
                }
              );
              
              if (response.data?.profile) {
                const updatedProfile = response.data.profile;
                dispatch({ type: "SET_USER_DATA", payload: updatedProfile });
                // Store only non-sensitive profile data
                storeProfileSafely(updatedProfile);
                setIsInterestsRequired(false);
                setInterestsDismissed(true);
                setInterestsDialogOpen(false);
                
                dispatch({
                  type: "SET_POPUP",
                  payload: {
                    text: "Your interests have been saved successfully!",
                    type: "success",
                  },
                });
                setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), popupTimer);
              }
            } catch (error) {
              console.error("Error saving interests:", error);
              dispatch({
                type: "SET_POPUP",
                payload: {
                  text: error.response?.data?.error || "Failed to save interests",
                  type: "error",
                },
              });
              setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), popupTimer);
            }
          }}
          initialInterests={userData?.most_interested || []}
          isRequired={isInterestsRequired}
        />
      )}
    </>
  );
};

const App = () => {
  const dispatch = useDispatch();
  const [profile, setProfile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true); // Start open by default
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLocationAuth, setIsLocationAuth] = useState(false);

  const popupTxt = useSelector((state) => state.popupTxt);
  const authorized = useSelector((state) => state.authorized);
  const token = useSelector((state) => state.token);
  const userData = useSelector((state) => state.user);

  useEffect(() => {
    // Check website configuration first - only check in_maintenance for redirects
    const checkWebsiteConfiguration = async () => {
      try {
        const response = await axios.get(`${API_URL}/security/website-configuration`);
        if (response.data && response.data.in_maintenance) {
          // If site is in maintenance, redirect to login page which will show maintenance message
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return;
        }
      } catch (error) {
        console.error("Error checking website configuration:", error);
        // On error, continue normally (fail open)
      }
    };
    checkWebsiteConfiguration();

    // SECURITY: Get token from Amplify session (cookies) instead of localStorage
    const checkAuth = async () => {
      try {
        const { getAccessToken } = await import('./utils/authUtils');
        
        const token = await getAccessToken();
        if (token) {
          // SECURITY: Token stored in httpOnly cookies, not sent in Authorization header
          // Backend reads from cookies automatically
          dispatch({ type: "SET_TOKEN", payload: token });
          
          // Check if we have stored profile data to use while loading (non-sensitive data only)
          const storedProfile = getStoredProfile();
          if (storedProfile) {
            // Use stored profile immediately to avoid showing "Guest"
            dispatch({ type: "SET_USER_DATA", payload: storedProfile });
            dispatch({ type: "SET_AUTHORIZED", user: storedProfile });
          }
          
          // Set token in process to true while fetching fresh data
          dispatch({ type: "SET_TOKEN_IN_PROCESS", payload: true });
          
          // SECURITY: Cookies are sent automatically, no Authorization header needed
          // Always fetch profile from server on refresh to get latest data
          axios
            .get(API_URL + `/profile`, {
              withCredentials: true, // Ensure cookies are sent
            })
            .then((res) => {
              const profileData = { ...res.data.profile };
              // Store the fetched profile in Redux
              dispatch({ type: "SET_USER_DATA", payload: profileData });
              // Store only non-sensitive profile data in sessionStorage (for UX only, not security)
              // Note: This is for display purposes only, not for authentication
              // sessionStorage is cleared on tab close, reducing XSS impact
              storeProfileSafely(profileData);
              
              // Only set token in process to false after we have user data
              dispatch({ type: "SET_TOKEN_IN_PROCESS", payload: false });
              dispatch({ type: "SET_REDIRECT_PATH", payload: null });
              dispatch({ type: "SET_AUTHORIZED", user: profileData });
            })
            .catch((error) => {
              dispatch({ type: "SET_TOKEN_IN_PROCESS", payload: false });
              dispatch({ type: "SET_UNAUTHORIZED", user: null });
              dispatch({ type: "SET_REDIRECT_PATH", payload: null });
            });
        } else {
          dispatch({ type: "SET_TOKEN_IN_PROCESS", payload: false });
          dispatch({ type: "SET_UNAUTHORIZED" });
        }
      } catch (error) {
        // Not authenticated
        dispatch({ type: "SET_TOKEN_IN_PROCESS", payload: false });
        dispatch({ type: "SET_UNAUTHORIZED" });
      }
    };
    
    checkAuth();
  }, [dispatch]);

  return (
    <ThemeProvider theme={theme}>
      <BrowserRouter>
        <AppContent
          setProfile={setProfile}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          isMobile={isMobile}
          popupTxt={popupTxt}
          isLocationAuth={isLocationAuth}
          authorized={authorized}
          setIsLocationAuth={setIsLocationAuth}
        />
        <AppContentWithDialog />
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;
